import React, { type ReactElement, useEffect } from 'react'
import { Box } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'

import { Button, Icon, IconButton, Typography } from '~components/internal'
import PhoneField from '~components/internal/PhoneField'
import { typography } from '~tokens/internal'
import { phoneValid } from '~types'

import '../PhoneInputTable.scss'

interface CountryOption {
  code: string
  name: string
  emoji: string
  dialCode: string
  maxDigits: number
}

const countryOptions: CountryOption[] = [
  { code: 'US', name: 'United States', emoji: '🇺🇸', dialCode: '+1', maxDigits: 10 },
]

interface PhoneInputRow {
  id: string
  countryCode: string
  phoneNumber: string
  extension: string
  isValid: boolean
}

type PhoneInputTableProps = {
  initialPhoneNumbers?: string[]
  setPhoneNumbers: React.Dispatch<React.SetStateAction<string[]>>
  onValidationChange?: (isValid: boolean) => void
}

const validatePhone = (phone: string): boolean => {
  if (phone === '') return true
  return phoneValid(phone)
}

const parsePhoneNumber = (phone: string): PhoneInputRow => {
  let number = phone
  let countryCode = 'US'
  for (const country of countryOptions) {
    if (number.startsWith(country.dialCode)) {
      countryCode = country.code
      number = number.slice(country.dialCode.length)
      break
    }
  }
  const [phoneNumber, extension] = number.split('x')
  return {
    id: uuidv4(),
    countryCode,
    phoneNumber: phoneNumber || '',
    extension: extension || '',
    isValid: true,
  }
}

const PhoneInputTable = ({
  initialPhoneNumbers,
  setPhoneNumbers,
  onValidationChange,
}: PhoneInputTableProps): ReactElement => {
  const [inputRows, setInputRows] = React.useState<PhoneInputRow[]>(
    initialPhoneNumbers && initialPhoneNumbers.length > 0
      ? initialPhoneNumbers.map(parsePhoneNumber)
      : [{ id: uuidv4(), countryCode: 'US', phoneNumber: '', extension: '', isValid: true }],
  )

  useEffect(() => {
    const isValid = inputRows.every((row) => row.isValid || row.phoneNumber === '')
    onValidationChange?.(isValid)
  }, [inputRows, onValidationChange])

  const addPhoneInput = (): void => {
    setInputRows((prev) => [
      ...prev,
      { id: uuidv4(), countryCode: 'US', phoneNumber: '', extension: '', isValid: true },
    ])
  }

  const removePhoneInput = (id: string): void => {
    const newRows = inputRows.filter((row) => row.id !== id)
    setInputRows(newRows)
    syncPhoneNumbers(newRows)
  }

  const syncPhoneNumbers = (rows: PhoneInputRow[]): void => {
    const validPhones = rows
      .filter((row) => row.isValid && row.phoneNumber.length > 0)
      .map((row) => {
        const country = countryOptions.find((c) => c.code === row.countryCode)
        const fullNumber = row.extension ? `${row.phoneNumber}x${row.extension}` : row.phoneNumber
        return `${country?.dialCode ?? '+1'}${fullNumber}`
      })
    setPhoneNumbers(validPhones)
  }

  const handleFieldChange = (
    id: string,
    field: 'countryCode' | 'phoneNumber' | 'extension',
    newValue: string,
  ): void => {
    const newRows = inputRows.map((row) => {
      if (row.id !== id) return row
      const updated = { ...row, [field]: newValue }
      return updated
    })
    setInputRows(newRows)
    syncPhoneNumbers(newRows)
  }

  const handleBlur = (id: string): void => {
    const newRows = inputRows.map((row) => {
      if (row.id !== id) return row
      return { ...row, isValid: validatePhone(row.phoneNumber) }
    })
    setInputRows(newRows)
    syncPhoneNumbers(newRows)
  }

  return (
    <Box className='phone-input-table'>
      <Box className='phone-input-table__list'>
        <Typography variant='subtitle2' color='textSecondary'>
          Phone Number
        </Typography>
        {inputRows.map((row) => {
          return (
            <Box key={row.id}>
              <Box className='phone-input-table__row'>
                <PhoneField
                  error={row.phoneNumber !== '' && !row.isValid}
                  errorText='Invalid phone number'
                  numberProps={{
                    value: row.phoneNumber,
                    onChange: (number) => handleFieldChange(row.id, 'phoneNumber', number),
                    onBlur: () => handleBlur(row.id),
                  }}
                  extensionProps={{
                    value: row.extension,
                    onChange: (extension) => handleFieldChange(row.id, 'extension', extension),
                  }}
                />
                {inputRows.length > 1 && (
                  <IconButton size='small' onClick={() => removePhoneInput(row.id)}>
                    <Icon name='Remove' size={typography.fontSize.lg} />
                  </IconButton>
                )}
              </Box>
            </Box>
          )
        })}
        <Button
          variant='text'
          size='small'
          width='fit'
          startIcon={<Icon name='AddCircleOutlined' />}
          onClick={addPhoneInput}
        >
          Add another
        </Button>
      </Box>
    </Box>
  )
}

export default PhoneInputTable
